import { Component, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { LoginService } from '../login/login.service';
import { LoaderService } from 'src/app/loader/loader.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ToastrService } from 'ngx-toastr';
import { SharedsService } from 'src/app/shareds/shareds.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
declare const window: any;

@Component({
  selector: 'app-self-signup',
  templateUrl: './self-signup.component.html',
  styleUrls: ['./self-signup.component.scss'],
})
export class SelfSignupComponent {
  translate: TranslateService;
  selectedSignupOption = 'self';
  choosePlan = false;
  isFormSubmitted = false;
  defaultLanguage: any = 'en';
  UserForm: UntypedFormGroup;
  passwordValidate = false;
  validationMsg: any;
  loginService: LoginService;
  clickValidate = false;
  showInfo = false;
  loader: LoaderService;
  submitted = false;
  sharedService: SharedsService;
  userInfo: any;
  resetForm = false;

  constructor(
    private readonly deviceService: DeviceDetectorService,
    private readonly injector: Injector,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
  ) {
    this.translate = this.injector.get<TranslateService>(TranslateService);
    this.translate.setDefaultLang('en');
    this.translate.use(this.defaultLanguage ? this.defaultLanguage : 'en');
    this.loginService = this.injector.get<LoginService>(LoginService);
    this.loader = this.injector.get<LoaderService>(LoaderService);
    this.sharedService = this.injector.get<SharedsService>(SharedsService);

    this.UserForm = this.formBuilder.group(
      {
        licensekey: ['', Validators.required],
        email: ['', Validators.required],
        company: ['', Validators.required],
        username: ['', Validators.required],
        password: ['', Validators.required],
        confirm: ['', Validators.required],
      },
      { validator: this.ConfirmPasswordValidator('password', 'confirm') },
    );
  }

  ngOnInit() {
    window.sendIdTokenToServer = this.sendIdTokenToServer.bind(this);
    const code = this.route?.snapshot?.queryParams?.['code'];
    const state = this.route?.snapshot?.queryParams?.state;
    if (state === 'google_signup') {
      this.sendIdTokenToServer(code, state);
    }
    localStorage.setItem('Language', this.defaultLanguage);
  }

  ConfirmPasswordValidator(controlName: string, matchingControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmPasswordValidator
      ) {
        this.passwordValidate = true;
        return;
      }
      if (control.value !== matchingControl.value) {
        this.passwordValidate = true;
        matchingControl.setErrors({ confirmPasswordValidator: true });
      } else {
        this.passwordValidate = false;
        matchingControl.setErrors(null);
      }
    };
  }

  handleReceivedData(data) {
    this.isFormSubmitted = data.valid;
    this.loader.signUpMessage(true);
    if (data.signUpData && data.signUp === true) {
      this.loginService.oboardSignup(data.signUpData).subscribe({
        next: (data) => {
          this.signUpApiResp(data, 'selfOnboard');
        },
        error: (err) => {
          console.error('Error flistPlan:', err);
        },
      });
    }
  }

  selectedSignupChange(value) {
    this.selectedSignupOption = value;
  }

  setValidationinfo(data) {
    this.UserForm?.controls['licensekey'].disable();
    this.UserForm?.controls['email'].setValue(
      data.info.email ? data.info.email : '',
    );
    this.UserForm?.controls['username'].setValue(
      data.info.email ? data.info.email : '',
    );
    this.UserForm?.controls['company'].setValue(
      data.info.company ? data.info.company : '',
    );
  }

  verifyKey() {
    this.validationMsg = '';
  }

  licenseValidation() {
    this.clickValidate = true;
    if (this.UserForm?.controls['licensekey'].invalid) {
      return;
    }
    const key = {
      token: this.UserForm?.controls['licensekey'].value,
    };
    this.loginService.licenseValidation(key).subscribe((data) => {
      if (data?.status && Object.keys(data.info).length) {
        this.showInfo = true;
        this.clickValidate = false;
        this.setValidationinfo(data);
      } else {
        this.validationMsg = data.info;
        this.showInfo = false;
        this.clickValidate = true;
      }
    });
  }

  onSignUp() {
    this.submitted = true;
    const browser_details = this.deviceService.getDeviceInfo();
    if (this.UserForm?.invalid) {
      return;
    }
    const info = {
      company: this.UserForm?.controls['company'].value,
      userName: this.UserForm?.controls['username'].value,
      password: this.UserForm?.controls['password'].value,
      token: this.UserForm?.controls['licensekey'].value,
      browser_details: browser_details,
    };
    this.loader.display(true);
    this.loader.signUpMessage(true);
    this.loginService.userSignup(info).subscribe((response) => {
      this.signUpApiResp(response, '');
    });
  }

  signUpApiResp(response, status) {
    if (response && response.status === true) {
      this.loader.signUpMessage(false);
      this.toastr.info('', this.translate.instant('toastr.account_created'));
      this.setValues(response.data);
      localStorage.setItem(
        'channelDetails',
        JSON.stringify(response.data.channelDetails),
      );
      localStorage.setItem(
        'developerId',
        JSON.stringify(response.data.developerId),
      );
      if (status === 'selfOnboard') {
        this.router.navigate([environment.dashboardPrefix + '/settings'], {
          state: { action: 'self-onboard' },
        });
      } else {
        this.router.navigate([environment.dashboardPrefix + '/settings'], {
          state: { action: 'license-key' },
        });
      }
      this.dialog.closeAll();
      this.submitted = false;
    } else {
      this.resetForm = true;
      this.toastr.error(response?.info);
      this.loader.display(false);
      this.onClose();
    }
  }

  setValues(data) {
    const theme = data.user_data?.['appSettings']?.['projectDetail']?.theme;
    localStorage.setItem('theme', theme || 'black-style.css');
    sessionStorage.setItem('loggedin', 'true');
    const allowedAppIds = data.user_data?.appid;
    if (data.user_data?.appid?.length > 0) {
      data.user_data.appid = data.user_data.appid[0];
    }
    data.user_data.wabaAccounts = data?.user_data?.['appSettings'][
      'projectDetail'
    ]['wabaAccounts']
      ? data.user_data['appSettings']['projectDetail']['wabaAccounts']
      : [];
    localStorage.setItem('user', JSON.stringify(data.user_data));
    localStorage.setItem('allowedAppIds', allowedAppIds);
    if (data.user_data.companyNameLogoUrl) {
      localStorage.setItem(
        'companyNameLogoUrl',
        JSON.stringify(data.user_data.companyNameLogoUrl),
      );
    }
    if (data.user_data.companyName) {
      localStorage.setItem(
        'companyName',
        JSON.stringify(data.user_data.companyName),
      );
    }

    localStorage.setItem('access-token', JSON.stringify(data.user_data.token));

    this.sharedService.appId = data?.user_data?.appid ?? '';
    this.sharedService.token = data?.user_data?.token ?? '';

    sessionStorage.setItem('appid', this.sharedService.appId);

    if (data.user_data.companyNameLogoUrl) {
      localStorage.setItem(
        'companyNameLogoUrl',
        JSON.stringify(data.user_data.companyNameLogoUrl),
      );
    }
    if (data.user_data.companyName) {
      localStorage.setItem(
        'companyName',
        JSON.stringify(data.user_data.companyName),
      );
    }
    if (data.user_data.name) {
      localStorage.setItem('name', JSON.stringify(data.user_data.name));
    }
    if (data.user_data.address) {
      localStorage.setItem('address', JSON.stringify(data.user_data.address));
    }
  }

  onClose() {
    this.dialog.closeAll();
    this.showInfo = false;
    this.UserForm?.controls['licensekey'].enable();
    this.UserForm?.reset();
    this.clickValidate = false;
    this.submitted = false;
    this.validationMsg = '';
  }

  sendIdTokenToServer(googleUser, state) {
    const idToken = googleUser;
    const arg = { idToken, type: state };
    if (idToken) {
      this.loginService.sendGoogleToken(arg).subscribe((data) => {
        if (data?.success && state === 'google_signup') {
          console.log('datadata', data);
          this.loader.display(false);
          this.userInfo = data?.user_data;
          this.userInfo.state = 'google_signup';
          this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParams: {},
          });
          console.log('userInfo', this.userInfo);
        } else {
          const error = data.message
            ? data.message
            : 'Sorry something went wrong';
          this.toastr.error('', error);
          console.log('error', error);
          this.loader.display(false);
          this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParams: {},
          });
        }
      });
    } else {
      this.loader.display(false);
      this.toastr.error('', this.translate.instant('toastr.something_wrong'));
    }
  }
}
